import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { CardioExercise, CardioSession, CreateCardioSessionInput } from '../services/types';
import { CardioExerciseService } from '../services/cardioExerciseService';
import { CardioSessionService } from '../services/cardioSessionService';
import { getImageUrl } from '../services/api';
import { API_BASE_URL } from '../config';

interface CardioSessionLoggerProps {
  onSessionLogged?: (session: CardioSession) => void;
  onClose?: () => void;
}

const CardioSessionLogger: React.FC<CardioSessionLoggerProps> = ({ onSessionLogged, onClose }) => {
  const [cardioExercises, setCardioExercises] = useState<CardioExercise[]>([]);
  const [selectedExercise, setSelectedExercise] = useState<CardioExercise | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [formData, setFormData] = useState({
    durationMinutes: '',
    heartRateAvg: '',
    heartRatePeak: '',
    distance: '',
    distanceUnit: 'km',
    avgSpeed: '',
    caloriesBurned: '',
    notes: '',
  });

  const DISTANCE_UNITS = ['km', 'miles', 'floors', 'meters'];

  useEffect(() => {
    loadCardioExercises();
  }, []);

  const loadCardioExercises = async () => {
    try {
      setLoading(true);
      setError(null);
      const exercises = await CardioExerciseService.getAll();
      console.log('Loaded cardio exercises:', exercises);
      setCardioExercises(exercises);
      if (exercises.length > 0) {
        setSelectedExercise(exercises[0]);
      } else {
        setError('Няма налични кардио упражнения. Проверете дали сървърът е стартиран и миграциите са изпълнени.');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      setError(`Грешка при зареждане на упражнения: ${errorMsg}`);
      console.error('CardioExerciseService error details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExerciseChange = (exerciseId: number) => {
    const exercise = cardioExercises.find(e => e.id === exerciseId);
    if (exercise) {
      setSelectedExercise(exercise);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('image', file);

      const response = await fetch(`${API_BASE_URL}/api/upload/cardio-result`, {
        method: 'POST',
        body: formDataToSend,
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to upload image' }));
        setError(errorData.error || 'Failed to upload image');
        return;
      }

      const data = await response.json();
      setResultImage(data.url);
      setError(null);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to upload image';
      setError(errorMsg);
      console.error('Image upload error:', err);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!resultImage) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/delete/cardio-result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: resultImage }),
        credentials: 'include',
      });

      if (response.ok) {
        setResultImage(null);
        setError(null);
      } else {
        setError('Failed to delete image');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete image');
    }
  };

  const validateForm = (): boolean => {
    if (!selectedExercise) {
      setError('Please select an exercise');
      return false;
    }
    if (!formData.durationMinutes) {
      setError('Please enter duration');
      return false;
    }
    return true;
  };

  const handleSaveSession = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const now = new Date();

      const input: CreateCardioSessionInput = {
        sessionDate: now.toISOString(),
        durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : undefined,
        heartRateAvg: formData.heartRateAvg ? parseInt(formData.heartRateAvg) : undefined,
        heartRatePeak: formData.heartRatePeak ? parseInt(formData.heartRatePeak) : undefined,
        distance: formData.distance || undefined,
        distanceUnit: formData.distance ? formData.distanceUnit : undefined,
        avgSpeed: formData.avgSpeed || undefined,
        caloriesBurned: formData.caloriesBurned ? parseInt(formData.caloriesBurned) : undefined,
        notes: formData.notes || undefined,
        resultImage: resultImage || undefined,
        cardioExercise: `/api/cardio_exercises/${selectedExercise?.id}`,
      };

      const session = await CardioSessionService.create(input);
      setError(null);

      // Reset form
      setFormData({
        durationMinutes: '',
        heartRateAvg: '',
        heartRatePeak: '',
        distance: '',
        distanceUnit: 'km',
        avgSpeed: '',
        caloriesBurned: '',
        notes: '',
      });
      setResultImage(null);

      if (onSessionLogged) {
        onSessionLogged(session);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save session');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && cardioExercises.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto' }}>
      <CardContent>
        <Typography variant="h5" component="div" sx={{ mb: 3 }}>
          Log Cardio Session
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Exercise Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Exercise</InputLabel>
              <Select
                value={selectedExercise?.id || ''}
                label="Exercise"
                onChange={(e) => handleExerciseChange(e.target.value as unknown as number)}
              >
                {cardioExercises.map(exercise => (
                  <MenuItem key={exercise.id} value={exercise.id}>
                    {exercise.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Duration */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration (minutes)"
              type="number"
              value={formData.durationMinutes}
              onChange={(e) => handleFormChange('durationMinutes', e.target.value)}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Heart Rate Average */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Average Heart Rate (bpm)"
              type="number"
              value={formData.heartRateAvg}
              onChange={(e) => handleFormChange('heartRateAvg', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Heart Rate Peak */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Peak Heart Rate (bpm)"
              type="number"
              value={formData.heartRatePeak}
              onChange={(e) => handleFormChange('heartRatePeak', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Distance */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Distance"
              type="number"
              value={formData.distance}
              onChange={(e) => handleFormChange('distance', e.target.value)}
              inputProps={{ step: '0.01', min: 0 }}
            />
          </Grid>

          {/* Distance Unit */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Distance Unit</InputLabel>
              <Select
                value={formData.distanceUnit}
                label="Distance Unit"
                onChange={(e) => handleFormChange('distanceUnit', e.target.value)}
              >
                {DISTANCE_UNITS.map(unit => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Average Speed */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Average Speed"
              type="number"
              value={formData.avgSpeed}
              onChange={(e) => handleFormChange('avgSpeed', e.target.value)}
              inputProps={{ step: '0.01', min: 0 }}
              helperText="km/h or miles/h"
            />
          </Grid>

          {/* Calories Burned */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Calories Burned"
              type="number"
              value={formData.caloriesBurned}
              onChange={(e) => handleFormChange('caloriesBurned', e.target.value)}
              inputProps={{ min: 0 }}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => handleFormChange('notes', e.target.value)}
            />
          </Grid>

          {/* Image Upload */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: 'action.hover',
                border: '2px dashed',
                borderColor: 'divider',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.selected' },
                position: 'relative',
              }}
            >
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="cardio-image-upload"
                type="file"
                onChange={handleImageUpload}
                disabled={uploadingImage}
              />
              <label htmlFor="cardio-image-upload" style={{ cursor: 'pointer', display: 'block' }}>
                {uploadingImage ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <CloudUploadIcon sx={{ fontSize: 40, color: '#666', mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      Click to upload result image
                    </Typography>
                  </>
                )}
              </label>
            </Paper>
          </Grid>

          {/* Image Preview */}
          {resultImage && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, position: 'relative', maxHeight: 300, overflow: 'auto' }}>
                <img
                  src={getImageUrl(resultImage)}
                  alt="Result"
                  style={{ maxWidth: '100%', maxHeight: 250, borderRadius: 4 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  aria-label="Delete uploaded result image"
                  data-testid="cardio-image-delete-button"
                  onClick={handleDeleteImage}
                  sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.9)' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Paper>
            </Grid>
          )}

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              {onClose && (
                <Button variant="outlined" onClick={onClose} startIcon={<CloseIcon />}>
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleSaveSession}
                disabled={loading || !selectedExercise}
                startIcon={<SaveIcon />}
              >
                Save Session
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CardioSessionLogger;
